import Cookies from 'js-cookie';
import { omit, pick } from 'lodash';

import Storage from './base';

class CookieStorage<T> extends Storage<T> {
  protected _key: string;

  constructor(key: string) {
    super();

    this._key = key;
  }

  protected _getKey(): Promise<string> | string {
    return this._key;
  }

  async get<K extends keyof T = keyof T>(
    keys?: K[]
  ): Promise<Pick<Partial<T>, K>> {
    const key = await this._getKey();

    const items = Cookies.get(key);
    if (items == null) {
      return {} as Partial<T>;
    }

    const obj: Partial<T> = JSON.parse(items);
    if (keys != null) {
      return pick(obj, keys);
    }

    return obj;
  }

  async set(items: Partial<T>): Promise<void> {
    const key = await this._getKey();

    const current = await this.get();
    Cookies.set(key, JSON.stringify({ ...current, ...items }), {
      httpOnly: true, // Prevents JavaScript access
      secure: true, // Only send over HTTPS
      sameSite: 'Strict', // Protect against CSRF
      expires: 7, // Expires in 7 days
    });
  }

  async remove(keys?: (keyof T)[]): Promise<void> {
    const key = await this._getKey();

    if (keys == null) {
      Cookies.remove(key);
      return;
    }

    const current = await this.get();
    const updated = omit(current, keys);
    Cookies.set(key, JSON.stringify(updated));
  }
}

export default CookieStorage;
